import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, Send, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';

const ChatPanel = ({ 
  isVisible, 
  onClose, 
  activeSection, 
  cvService, 
  initialQuestion = null 
}) => {
  const [userQuestion, setUserQuestion] = useState('');
  const [conversation, setConversation] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom of chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  // Handle initial question
  useEffect(() => {
    if (initialQuestion && activeSection) {
      handleQuestionSubmit(null, initialQuestion);
    }
  }, [initialQuestion, activeSection]);

  // Clear conversation when section changes
  useEffect(() => {
    if (activeSection) {
      setConversation([]);
    }
  }, [activeSection]);

  const handleQuestionSubmit = async (e, questionText = null) => {
    if (e) e.preventDefault();
    
    const question = questionText || userQuestion.trim();
    if (!question) return;
    
    // Add user question to conversation
    const newConversation = [...conversation, { type: 'user', text: question }];
    setConversation(newConversation);
    setUserQuestion('');
    setIsLoading(true);
    
    // Simulate thinking time
    setTimeout(() => {
      let response = cvService.locale === 'fr' ? 
        "Je n'ai pas d'information spécifique sur cette question. Pourriez-vous reformuler ou demander autre chose ?" :
        "I don't have specific information about this question. Could you rephrase or ask something else?";
      
      if (activeSection) {
        response = cvService.findAnswerForQuestion(activeSection, question);
      }
      
      setConversation([...newConversation, { type: 'ai', text: response }]);
      setIsLoading(false);
    }, 800);
  };

  const getSuggestedQuestions = () => {
    if (!activeSection) return [];
    
    const qa = cvService.getSectionQA(activeSection);
    return qa.slice(0, 3).map(item => item.question);
  };

  const getSectionTitle = () => {
    if (!activeSection) {
      return cvService.locale === 'fr' ? 'Posez-moi une question' : 'Ask me a question';
    }
    
    const experience = cvService.getExperienceById(activeSection);
    if (experience) {
      const organization = cvService.getTranslatedValue(experience.organization);
      const title = cvService.getTranslatedValue(experience.title);
      return `${organization} - ${title}`;
    }
    
    return activeSection;
  };

  if (!isVisible) return null;

  const suggestedQuestions = getSuggestedQuestions();

  return (
    <Card className="w-full overflow-hidden flex flex-col bg-space-900/80 backdrop-blur-md border-hair shadow-xl rounded-xl" style={{ height: '400px' }}>
      {/* Header */}
      <CardHeader className="bg-space-700 text-white py-3 rounded-t-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <MessageCircle size={20} />
            <CardTitle className="text-sm font-medium">
              {cvService.locale === 'fr' ? 'À propos de: ' : 'About: '}
              {getSectionTitle()}
            </CardTitle>
          </div>
          
          <Button 
            variant="ghost" 
            size="icon"
            onClick={onClose}
            className="text-white hover:bg-space-700/60 h-8 w-8"
          >
            <X size={16} />
          </Button>
        </div>
      </CardHeader>
      
      {/* Content */}
      <CardContent className="flex-1 overflow-y-auto p-4">
        {activeSection ? (
          <>
            <p className="text-muted-foreground mb-4 text-sm">
              {cvService.locale === 'fr' ? 
                'Sélectionnez une question ou posez la vôtre concernant cette section:' :
                'Select a question or ask your own about this section:'}
            </p>
            
            {/* Suggested Questions */}
            {suggestedQuestions.length > 0 && (
              <div className="mb-4 space-y-2">
                {suggestedQuestions.map((question, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => handleQuestionSubmit(null, question)}
                    className="w-full justify-start h-auto p-3 text-left text-sm whitespace-normal"
                  >
                    {question}
                  </Button>
                ))}
              </div>
            )}
          </>
        ) : (
          <p className="text-muted-foreground mb-4 text-sm">
            {cvService.locale === 'fr' ? 
              'Sélectionnez une section du CV pour poser des questions spécifiques ou posez directement votre question.' :
              'Select a CV section to ask specific questions or ask your question directly.'}
          </p>
        )}
        
        {/* Conversation */}
        {conversation.length > 0 && (
          <>
            <Separator className="my-4" />
            <div className="space-y-4">
              {conversation.map((message, index) => (
                <div 
                  key={index} 
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div 
                    className={`max-w-[85%] rounded-lg px-4 py-2 text-sm ${
                      message.type === 'user' 
                        ? 'bg-space-700 text-white rounded-br-none' 
                        : 'bg-space-700 text-ink-muted rounded-bl-none'
                    }`}
                  >
                    <p className="leading-relaxed">{message.text}</p>
                  </div>
                </div>
              ))}
              
              {/* Loading indicator */}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-space-700 rounded-lg px-4 py-2 rounded-bl-none">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 bg-space-600 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-space-600 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 bg-space-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          </>
        )}
      </CardContent>
      
      {/* Input */}
      <div className="border-t border-hair p-3 bg-space-900/40 rounded-b-xl">
        <form onSubmit={handleQuestionSubmit} className="flex items-center space-x-2">
          <Input
            type="text"
            value={userQuestion}
            onChange={(e) => setUserQuestion(e.target.value)}
            placeholder={cvService.locale === 'fr' ? 'Posez votre question...' : 'Ask your question...'}
            className="flex-1 text-sm"
            disabled={isLoading}
          />
          <Button 
            type="submit" 
            size="icon"
            disabled={isLoading || !userQuestion.trim()}
          >
            <Send size={16} />
          </Button>
        </form>
      </div>
    </Card>
  );
};

export default ChatPanel;
